// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { FormField, Knowledge, Form, CheckboxWithInputOptions, LogicOptions } from '../types/form';
import { 
  isCheckboxWithInputOptions, 
  isLogicOptions
} from '../types/guards';
import { 
  Edit2, 
  Trash2, 
  Save, 
  X, 
  GripVertical,
  Type,
  Hash,
  Phone,
  Mail,
  CheckSquare,
  Circle,
  FileText,
  Calendar,
  Upload,
  Image,
  Table,
  Heading,
  ToggleLeft,
  Calculator,
  ChevronDown,
  Video,
  Wifi,
  CalendarClock
} from 'lucide-react';

// Import Field Editors
import { ImageFieldEditor } from './form-fields/ImageFieldEditor';
import { MultiRowFieldEditor } from './form-fields/MultiRowFieldEditor';
import { HeaderFieldEditor } from './form-fields/HeaderFieldEditor';
import { TextareaFieldEditor } from './form-fields/TextareaFieldEditor';
import { CheckboxWithInputFieldEditor } from './form-fields/CheckboxWithInputFieldEditor';
import { OptionsFieldEditor } from './form-fields/OptionsFieldEditor';
import LogicFieldEditor from './form-fields/LogicFieldEditor';
import { AVCheckFieldEditor } from './form-fields/AVCheckFieldEditor';
import { ConnectionCheckFieldEditor } from './form-fields/ConnectionCheckFieldEditor';
import { SchedulerFieldEditor } from './form-fields/SchedulerFieldEditor';
import { FieldPreview } from './form-fields/FieldPreview';

interface SortableFormFieldProps {
  field: FormField;
  index: number;
  onUpdateField: (fieldId: string, updates: Partial<FormField>) => void;
  onDeleteField: (fieldId: string) => void;
  form: Form;
  knowledge?: Knowledge[];
}

const fieldIcons = {
  text: Type,
  number: Hash,
  phone: Phone,
  email: Mail,
  dropdown: ChevronDown,
  checkbox: CheckSquare,
  radio: Circle,
  textarea: FileText,
  date: Calendar,
  file: Upload,
  image: Image,
  image_upload: Image,
  multi_row: Table,
  header: Heading,
  checkbox_with_input: ToggleLeft,
  logic: Calculator,
  av_check: Video,
  connection_check: Wifi,
  scheduler: CalendarClock
};

const SortableFormField: React.FC<SortableFormFieldProps> = ({
  field,
  index,
  onUpdateField,
  onDeleteField,
  form,
  knowledge = [],
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedField, setEditedField] = useState(field);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: field.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Reset editing if field type changes while editing (prevents data corruption)
  useEffect(() => {
    if (isEditing && field.field_type !== editedField.field_type) {
      setIsEditing(false);
      setEditedField(field);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.field_type, isEditing]);

  // Initialize editedField state when entering edit mode
  useEffect(() => {
    if (isEditing) {
        if (field.field_type === 'dropdown' || field.field_type === 'radio' || field.field_type === 'checkbox') {
        if (!editedField.options || typeof editedField.options !== 'object') {
            setEditedField(prev => ({
            ...prev,
            options: []
            }));
        } else if (!Array.isArray(editedField.options)) {
            // Convert options object to array
            const optionsArray = Object.entries(editedField.options)
            .map(([key, value]) => typeof value === 'string' ? value : key);

            // For radio/checkbox, keep as object with optionsList
            if (field.field_type === 'radio' || field.field_type === 'checkbox') {
            setEditedField(prev => ({
                ...prev,
                options: {
                optionsList: optionsArray
                } as any
            }));
            } else {
            // For dropdown, just use array
            setEditedField(prev => ({
                ...prev,
                options: optionsArray
            }));
            }
        }
        } else if (field.field_type === 'checkbox_with_input') {
        if (!editedField.options || typeof editedField.options !== 'object') {
            setEditedField(prev => ({
            ...prev,
            options: {
                inputType: 'text',
                inputPlaceholder: 'Enter value',
                customOptions: []
            }
            }));
        } else {
            // For checkbox_with_input, extract the checkbox options but preserve input settings
            const options = editedField.options as CheckboxWithInputOptions;
            if (isCheckboxWithInputOptions(editedField.options)) {
            const { inputType, inputPlaceholder, customOptions, ...checkboxOptions } = options;
            const optionsArray = Object.entries(checkboxOptions).map(([key, value]) => 
                typeof value === 'string' ? value : key
            );
            setEditedField(prev => ({
                ...prev,
                options: {
                inputType: inputType || 'text',
                inputPlaceholder: inputPlaceholder || 'Enter value',
                customOptions: optionsArray.length > 0 ? optionsArray : (customOptions || [])
                }
            }));
            }
        }
        } else if (field.field_type === 'logic') {
        // Initialize logic field options if not present, but preserve existing values
        setEditedField(prev => {
            const options = prev.options as LogicOptions;
            if (!prev.options || typeof prev.options !== 'object') {
            return {
                ...prev,
                options: {
                logicType: 'sum_checkbox_inputs',
                sourceFieldId: ''
                }
            };
            } else {
            // Ensure required properties exist while preserving saved values
            return {
                ...prev,
                options: {
                logicType: (prev.options && isLogicOptions(prev.options)) ? options.logicType || 'sum_checkbox_inputs' : 'sum_checkbox_inputs',
                sourceFieldId: (prev.options && isLogicOptions(prev.options)) ? options.sourceFieldId || '' : '',
                ...(prev.options || {})
                }
            };
            }
        });
        }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, field.field_type]);

  // Sync editedField with field prop changes (only when not editing to avoid losing user changes)
  useEffect(() => {
    if (!isEditing) {
      setEditedField(field);
    }
  }, [field, isEditing]);

  const handleSave = () => {
    if (field.field_type === 'dropdown' || field.field_type === 'radio' || field.field_type === 'checkbox') {
      if (editedField.knowledge) {
        // If using knowledge, keep the options as is
        onUpdateField(field.id, editedField);
      } else {
        // Get the options array (handle both array and object with optionsList)
        const optionsArray = Array.isArray(editedField.options)
          ? editedField.options.filter((opt: any) => opt && typeof opt === 'string' && opt.trim() !== '')
          : ((editedField.options?.optionsList || []) as string[]).filter((opt: string) => opt && opt.trim() !== '');

        // Convert array to object format (only if we have valid options)
        const optionsObject = optionsArray.length > 0 
          ? optionsArray.reduce((obj: any, value: string, index: number) => {
              obj[`option${index + 1}`] = value;
              return obj;
            }, {} as { [key: string]: string })
          : {};

        const updatedField = {
          ...editedField,
          options: optionsObject
        };
        console.log('[SortableFormField] Saving field:', {
          fieldType: field.field_type,
          layout_config: updatedField.layout_config,
          options: updatedField.options
        });
        onUpdateField(field.id, updatedField);
      }
    } else if (field.field_type === 'checkbox_with_input') {
      const options = editedField.options as CheckboxWithInputOptions;
      const inputType = options?.inputType || 'text';
      const inputPlaceholder = options?.inputPlaceholder || 'Enter value';
      
      if (editedField.knowledge) {
        // If using knowledge, preserve inputType and inputPlaceholder
        onUpdateField(field.id, {
          ...editedField,
          options: {
            ...editedField.options,
            inputType,
            inputPlaceholder
          }
        });
      } else {
        // If using custom options, convert array to object format but preserve input settings
        const optionsArray = options?.customOptions || [];
        
        const optionsObject = (optionsArray as string[]).reduce((obj, value, index) => {
          obj[`option${index + 1}`] = value;
          return obj;
        }, {} as { [key: string]: string });
        
        onUpdateField(field.id, {
          ...editedField,
          options: {
            ...optionsObject,
            inputType,
            inputPlaceholder
          }
        });
      }
    } else {
      onUpdateField(field.id, editedField);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedField(field);
    setIsEditing(false);
  };

  const onFieldChange = (updates: Partial<FormField>) => {
      setEditedField(prev => ({ ...prev, ...updates }));
  };

  const Icon = fieldIcons[field.field_type] || Type; // Fallback to Type icon if field type not found

  const renderFieldSpecificEditor = () => {
    switch (field.field_type) {
      case 'textarea':
        return <TextareaFieldEditor editedField={editedField} onFieldChange={onFieldChange} />;
      case 'image':
        return <ImageFieldEditor field={field} editedField={editedField} onFieldChange={onFieldChange} />;
      case 'header':
        return <HeaderFieldEditor editedField={editedField} onFieldChange={onFieldChange} />;
      case 'checkbox_with_input':
        return (
          <>
            <CheckboxWithInputFieldEditor editedField={editedField} onFieldChange={onFieldChange} />
            <OptionsFieldEditor field={field} editedField={editedField} onFieldChange={onFieldChange} knowledge={knowledge} />
          </>
        );
      case 'dropdown':
      case 'radio':
      case 'checkbox':
        return <OptionsFieldEditor field={field} editedField={editedField} onFieldChange={onFieldChange} knowledge={knowledge} />;
      case 'logic':
        return <LogicFieldEditor field={editedField} form={form} onFieldChange={onFieldChange} />;
      case 'multi_row':
        return <MultiRowFieldEditor field={field} editedField={editedField} onFieldChange={onFieldChange} knowledge={knowledge} />;
      case 'av_check':
        return <AVCheckFieldEditor editedField={editedField} onFieldChange={onFieldChange} />;
      case 'connection_check':
        return <ConnectionCheckFieldEditor editedField={editedField} onFieldChange={onFieldChange} />;
      case 'scheduler':
        return <SchedulerFieldEditor editedField={editedField} onFieldChange={onFieldChange} />;
      default:
        return null;
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="mb-4"
    >
      <Card className={`transition-all duration-200 ${
        isDragging 
          ? 'border-2 border-dashed border-primary shadow-lg' 
          : 'border-2 border-dashed border-border hover:border-primary/50'
      }`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-3">
              <div
                {...attributes}
                {...listeners}
                className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded"
              >
                <GripVertical className="w-4 h-4 text-muted-foreground" />
              </div>
              <Icon className="w-5 h-5 text-primary" />
              <div>
                <div className="font-medium">{field.label}</div>
                <div className="text-sm text-muted-foreground">{field.field_type}</div>
              </div>
              {field.is_required && (
                <span className="text-red-500 text-sm">*</span>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsEditing(true)}
                className="h-8 w-8 p-0"
              >
                <Edit2 className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDeleteField(field.id)}
                className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {isEditing ? (
            <div className="space-y-4 border-t pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor={`label-${field.id}`}>Label</Label>
                  <Input
                    id={`label-${field.id}`}
                    value={editedField.label}
                    onChange={(e) => setEditedField({ ...editedField, label: e.target.value })}
                    placeholder="Field label"
                  />
                </div>
                {field.field_type !== 'multi_row' && field.field_type !== 'header' && field.field_type !== 'checkbox_with_input' && field.field_type !== 'checkbox' && field.field_type !== 'av_check' && field.field_type !== 'connection_check' && field.field_type !== 'scheduler' && (
                  <div>
                    <Label htmlFor={`placeholder-${field.id}`}>Placeholder</Label>
                    <Input
                      id={`placeholder-${field.id}`}
                      value={editedField.placeholder || ''}
                      onChange={(e) => setEditedField({ ...editedField, placeholder: e.target.value })}
                      placeholder="Placeholder text"
                    />
                  </div>
                )}
              </div>

              {renderFieldSpecificEditor()}

              {field.field_type !== 'multi_row' && field.field_type !== 'header' && field.field_type !== 'checkbox_with_input' && field.field_type !== 'logic' && field.field_type !== 'scheduler' && (
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={`required-${field.id}`}
                    checked={editedField.is_required}
                    onChange={(e) => setEditedField({ ...editedField, is_required: e.target.checked })}
                    className="rounded"
                  />
                  <Label htmlFor={`required-${field.id}`}>Required field</Label>
                </div>
              )}

              <div className="flex items-center space-x-2">
                <Button onClick={handleSave} size="sm">
                  <Save className="w-4 h-4 mr-2" />
                  Save
                </Button>
                <Button onClick={handleCancel} variant="outline" size="sm">
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">
                <FieldPreview field={field} />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SortableFormField;
